import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MonitoringService } from '../../../../core/services/monitoring/legal-entity-client/monitoring.service';
import { PagedBankResponse } from '../../../../core/models/bank.model';

export interface PatientData {
  id: number;
  nomeCompleto: string;
  data_nascimento: string;
  cpf: string;
  rg: string;
  sexo: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  numero: string;
  peso: number;
  altura: number;
  imc: number;
  historico_clinico: string;
  consentimento_aceito: boolean;
  data_consentimento: string;
  convenios: Array<{
    id_convenio: number;
    descricao: string;
    num_identificacao: string;
  }>;
  doencas: Array<{
    doenca_id: number;
    doenca_descricao: string;
  }>;
  habitos: Array<{
    habito_id: number;
    habito_descricao: string;
  }>;
}

export interface MeasurementHistory {
  id: string;
  data_medicao: string;
  hora_medicao: string;
  valor: string;
  contexto: string;
  unidade_de_medida: string;
}

export interface AlertData {
  alerta_id: number;
  medicao_id: number;
  valor_medido: number;
  nivel_acionado: number;
  tempo_resposta_max: number;
  classificacao_id: number;
  criado_em: string;
  classificacao: string;
  paciente_id: number;
  paciente_nome: string;
  telefone_whatsapp: string;
  unidade_nome: string;
  contexto_nome: string;
  respondido_em: string | null;
  respondido_por_nome: string | null;
  observacoes: string | null;
  minutos_passados: number;
  status_tempo: string;
}

@Component({
  selector: 'details-modal-alert',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTooltipModule,
    MatChipsModule
  ],
  templateUrl: './details-modal-alert.component.html',
  styleUrls: ['./details-modal-alert.component.scss']
})
export class DetailsModalAlert implements OnInit, OnDestroy {
  alert!: AlertData;
  measurementHistory: MeasurementHistory[] = [];
  patientData: PatientData | null = null;
  loadingPatient = false;
  loadingHistory = false;

  @Output() response = new EventEmitter<any>();

  constructor(
    private dialogRef: MatDialogRef<DetailsModalAlert>,
    private monitoringService: MonitoringService,
    @Inject(MAT_DIALOG_DATA) public data: AlertData
  ) {}

  ngOnInit(): void {
    this.alert = this.data;
    if (this.alert?.paciente_id) {
      this.getPatientData();
      this.getHistoryData();
    }
  }

  ngOnDestroy(): void {
    // Dialog cleanup
  }

  getPatientData(): void {
    this.loadingPatient = true;
    this.monitoringService.getPatientbyId(this.alert.paciente_id).subscribe({
      next: (response: any) => {
        this.patientData = response.data;
        this.loadingPatient = false;
      },
      error: () => {
        this.loadingPatient = false;
      }
    });
  }

  getHistoryData(): void {
    this.loadingHistory = true;
    this.monitoringService.measuresByPatient(this.alert.paciente_id).subscribe({
      next: (response: any) => {
        this.measurementHistory = response.data || [];
        this.loadingHistory = false;
      },
      error: () => {
        this.loadingHistory = false;
      }
    });
  }

  formatDate(dateString?: string | null): string {
    if (!dateString) return '-';
    const cleanDate = dateString.split('T')[0].split(' ')[0];
    if (cleanDate.includes('-')) {
      return cleanDate.split('-').reverse().join('/');
    }
    return cleanDate;
  }

  formatDateTime(dateTimeString?: string | null): string {
    if (!dateTimeString) return 'Não informado';
    const normalized = dateTimeString.replace('T', ' ').trim();
    const parts = normalized.split(' ');
    const datePart = parts[0] ? this.formatDate(parts[0]) : '';
    let timePart = parts[1] || '';
    if (timePart && timePart !== 'undefined') {
      timePart = timePart.substring(0, 5);
      return `${datePart} às ${timePart}`;
    }
    return datePart || 'Não informado';
  }

  getTempoDecorrido(): string {
    if (this.alert?.minutos_passados !== undefined && this.alert?.minutos_passados !== null && this.alert.minutos_passados > 0) {
      const status = this.alert.status_tempo ? ` (${this.alert.status_tempo})` : '';
      return `${this.alert.minutos_passados} minutos${status}`;
    }
    if (this.alert?.criado_em) {
      try {
        const criadoEm = new Date(this.alert.criado_em.replace(' ', 'T'));
        const agora = new Date();
        const diffMs = agora.getTime() - criadoEm.getTime();
        if (!isNaN(diffMs) && diffMs > 0) {
          const diffMin = Math.floor(diffMs / (1000 * 60));
          if (diffMin < 60) return `${diffMin} min atrás`;
          const diffHoras = Math.floor(diffMin / 60);
          if (diffHoras < 24) return `${diffHoras}h atrás`;
          const diffDias = Math.floor(diffHoras / 24);
          return `${diffDias}d atrás`;
        }
      } catch (e) {}
    }
    return 'Recentemente';
  }

  getClassificacaoNome(): string {
    if (this.alert?.classificacao && this.alert.classificacao.trim() !== '') {
      return this.alert.classificacao;
    }
    switch (this.alert?.classificacao_id) {
      case 1: return 'Leve';
      case 2: return 'Moderado';
      case 3: return 'Crítico';
      case 4: return 'Crítico Severo';
      default: return 'Padrão';
    }
  }

  getClassificacaoBadgeClass(): string {
    switch (this.alert?.classificacao_id) {
      case 1: return 'badge-leve';
      case 2: return 'badge-moderado';
      case 3: return 'badge-critico';
      case 4: return 'badge-critico-severo';
      default: return 'badge-normal';
    }
  }

  getGlicemiaStatus(valor: number): { label: string; class: string; icon: string } {
    if (valor < 70) {
      return { label: 'Hipoglicemia', class: 'status-hipo', icon: 'trending_down' };
    }
    if (valor <= 140) {
      return { label: 'Glicemia Normal', class: 'status-normal', icon: 'check_circle' };
    }
    if (valor <= 180) {
      return { label: 'Atenção / Pré-Prandial Elevada', class: 'status-alerta', icon: 'trending_up' };
    }
    if (valor <= 250) {
      return { label: 'Hiperglicemia', class: 'status-hiper', icon: 'warning' };
    }
    return { label: 'Hiperglicemia Severa', class: 'status-severa', icon: 'error' };
  }

  calcularIdade(dataNascimento: string): number | string {
    if (!dataNascimento) return '-';
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    if (isNaN(nascimento.getTime())) return '-';
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  }

  getImcClass(imc: number): { label: string; colorClass: string } {
    if (!imc) return { label: '-', colorClass: '' };
    if (imc < 18.5) return { label: 'Abaixo do peso', colorClass: 'imc-baixo' };
    if (imc < 25) return { label: 'Peso ideal', colorClass: 'imc-ideal' };
    if (imc < 30) return { label: 'Sobrepeso', colorClass: 'imc-sobrepeso' };
    if (imc < 35) return { label: 'Obesidade I', colorClass: 'imc-obesidade' };
    return { label: 'Obesidade Severa', colorClass: 'imc-severa' };
  }

  formatTelefone(tel?: string): string {
    if (!tel) return 'Não informado';
    const clean = tel.replace(/\D/g, '');
    if (clean.length === 11) {
      return `(${clean.substring(0, 2)}) ${clean.substring(2, 7)}-${clean.substring(7)}`;
    }
    if (clean.length === 13 && clean.startsWith('55')) {
      return `+55 (${clean.substring(2, 4)}) ${clean.substring(4, 9)}-${clean.substring(9)}`;
    }
    return tel;
  }

  openWhatsapp(): void {
    if (!this.alert?.telefone_whatsapp) return;
    const cleanTel = this.alert.telefone_whatsapp.replace(/\D/g, '');
    const mensagem =
      `Olá ${this.alert.paciente_nome}, esta é a central do Glicosus. ` +
      `Estamos entrando em contato para avisar que a sua glicemia foi registrada com alteração ` +
      `(${this.alert.valor_medido} mg/dL).`;
    const url = `https://wa.me/${cleanTel}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  }

  close(): void {
    this.response.emit('close');
    this.dialogRef.close();
  }
}
